/**
 * * Style utilities for applying consistent styles across components
 */

import { isValidHexColor } from "./color-valid-check";

const normalizeHexToSixDigits = (hex) => {
  if (typeof hex !== "string") {
    return null;
  }

  const sanitizedValue = hex.trim();
  if (!sanitizedValue) {
    return null;
  }

  const withHash = sanitizedValue.startsWith("#")
    ? sanitizedValue
    : `#${sanitizedValue}`;

  if (!isValidHexColor(withHash)) {
    return null;
  }

  if (withHash.length === 4) {
    const [, r, g, b] = withHash;
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }

  if (withHash.length === 9) {
    return `#${withHash.slice(1, 7)}`.toLowerCase();
  }

  return withHash.toLowerCase();
};

export const hexToRgb = (hex) => {
  const normalizedHex = normalizeHexToSixDigits(hex);

  if (!normalizedHex) {
    return null;
  }

  return {
    r: parseInt(normalizedHex.slice(1, 3), 16),
    g: parseInt(normalizedHex.slice(3, 5), 16),
    b: parseInt(normalizedHex.slice(5, 7), 16),
  };
};

export const hexToRgbaHex = (hex, alpha = 1) => {
  const normalizedHex = hexToRgb(hex);
  if (!normalizedHex) {
    return null;
  }
  const { r, g, b } = normalizedHex;
  const normalizedAlpha = Math.min(Math.max(alpha, 0), 1);
  const a = Math.round(normalizedAlpha * 255)
    .toString(16)
    .padStart(2, "0");
  return `#${r.toString(16).padStart(2, "0")}${g
    .toString(16)
    .padStart(2, "0")}${b.toString(16).padStart(2, "0")}${a}`;
};

export const tabStylePrefix = "TabStyle";
export const tabStyleClasses = {
  root: `${tabStylePrefix}-root`,
  tabsFlexContainer: `${tabStylePrefix}-tabsFlexContainer`,
  indicator: `${tabStylePrefix}-indicator`,
  tabs: `${tabStylePrefix}-tabs`,
  tab: `${tabStylePrefix}-tab`,
  selectedTab: `${tabStylePrefix}-selectedTab`,
};

export const tabStyleApplier = ({ theme }) => ({
  /**
   * * Root tabs container - match header height (81px)
   */
  flexGrow: 1,
  boxShadow: "none",
  height: "81px",
  alignSelf: "stretch",
  backgroundColor: "transparent",

  /**
   * * Tabs flex container (holds all tabs)
   */
  "& .MuiTabs-flexContainer": {
    height: "81px",
  },

  /**
   * * Tab scroller wrapper
   */
  "& .MuiTabs-scroller": {
    height: "81px",
  },

  /**
   * * Tab indicator (underline)
   */
  "& .MuiTabs-indicator": {
    backgroundColor: "var(--primaryColor)",
  },

  /**
   * * Individual tab buttons
   */
  "& .MuiTab-root": {
    color: "rgba(0, 0, 0, 0.54)",
    textTransform: "none",
    height: "81px",
    minHeight: "81px",
    maxHeight: "81px",
    fontSize: "var(--customFontBodySizeMedium)",

    [theme.breakpoints.down("md")]: {
      height: "81px",
      minHeight: "81px",
      maxHeight: "81px",
    },
  },

  /**
   * * Selected tab
   */
  "& .MuiTab-root.Mui-selected": {
    color: "#000000",
  },
});
